import { Icon } from '@iconify/react'
import { useMutation } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { Fragment, useState } from 'react'
import { toast } from 'react-toastify'

import { logout } from '~features/sign-out/api'
import { NativeSidebar } from '~in-app/components/layout/console/native-sidebar'
import { CustomizeDropdownMenuItemProps, DropdownMenuComponent } from '~in-app/components/MenuDropdown'
import { Badge, Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '~in-app/components/shadcn-ui'
import { ThemeButton } from '~in-app/components/ThemeButton'
import { auth$ } from '~in-app/legend-state'

import packageJson from '../../../../package.json'
import { ChangePasswordDialog } from './ChangePasswordDialog'
export const Header = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      auth$.removeJWTToken()
      navigate({ to: '/' })
      toast.success('Logout successful', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    },
    onError: () => {
      auth$.removeJWTToken()
      navigate({ to: '/' })
      toast.error('Logout failed', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    },
  })
  const version = packageJson.version
  const handleLogout = async () => {
    logoutMutation.mutate()
  }

  const [isOpen, setIsOpen] = useState(false)

  const handleOpenDialog = () => {
    setIsOpen(true)
  }

  const dropdownItems: CustomizeDropdownMenuItemProps[] = [
    {
      label: 'Change Password',
      onClick: () => setIsOpen(true),
      icon: (
        <Icon
          height="16"
          icon="mdi:password-reset"
          width="16"
        />
      ),
    },
    {
      label: 'Logout',
      onClick: () => handleLogout(),
      icon: (
        <Icon
          height="16"
          icon="mdi:logout"
          width="16"
        />
      ),
    },
  ]
  return (
    <header
      className="sticky top-0 z-10 flex h-14 w-full items-center border-b-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70"
    >
      <div className="w-full px-6 py-2">
        <div className="flex items-center justify-between">
          <div>
            <NativeSidebar />

            <Breadcrumb>
              <BreadcrumbList>
                {
                  pathname.split('/').map((segment, index, array) => {
                    const isLast = index === array.length - 1
                    const isSecondLast = index === array.length - 2

                    const isConsole = array[1] === 'console'
                    const isDashboard = array[2] === 'dashboard'
                    const isList = segment === 'list'
                    const isLastIsList = array[array.length - 1] === 'list'
                    const isLastIsObjectId = /^[a-f\d]{24}$/i.test(segment)
                    const path = (() => {
                      if (index === 0) return ''
                      const prevSegment = array[index - 1]
                      const nextSegment = array[index + 1]
                      if (prevSegment === '') {
                        return '/console/dashboard'
                      }
                      if (prevSegment === 'console' || nextSegment !== undefined) {
                        return `/${array.slice(1, index + 1).join('/')}/list`
                      }
                      if (segment === 'edit' && array[array.length - 1] !== 'add') {
                        return ''
                      }
                      if (segment === 'duplicate' && array[array.length - 1] !== 'add') {
                        return ''
                      }
                      return array.slice(0, index + 1).join('/')
                    })()

                    const formatSegment = (segment: string, isConsole: boolean, index: number): string => {
                      if (segment === 'qr') {
                        return 'QR'
                      }
                      if (isConsole && index === 1) {
                        return 'Home'
                      }

                      const toCamelCase = (str: string) => {
                        return str
                          .toLowerCase()
                          .split(/[-_]/g)
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')
                      }
                      const formattedSegment = toCamelCase(segment)
                      return formattedSegment
                    }

                    const displaySegment = formatSegment(segment, isConsole, index)

                    if ((isList || isLastIsObjectId) && isLast) {
                      return null
                    }

                    if (index === 0 && isConsole) {
                      return null
                    }

                    return (
                      <Fragment key={path}>
                        <BreadcrumbItem>
                          {
                            isLast || segment === 'edit' || segment === 'duplicate'
                              ? (
                                  <BreadcrumbPage>{displaySegment}</BreadcrumbPage>
                                )
                              : (
                                  <BreadcrumbLink href={path}>{displaySegment}</BreadcrumbLink>
                                )
                          }
                        </BreadcrumbItem>

                        {!isLast && !(isSecondLast && (isLastIsList || segment === 'edit' || segment === 'duplicate')) && <BreadcrumbSeparator />}
                      </Fragment>
                    )
                  }).filter(Boolean)
                }
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="flex items-center gap-2">
            <Badge
              className="border-green-400 bg-inherit text-green-400"
              variant="outline"
            >
              {`v${version}`}
            </Badge>

            <ThemeButton />

            <DropdownMenuComponent
              className="rounded-full bg-transparent p-2 hover:opacity-100"
              items={dropdownItems}
              loginLabel={`${auth$.firstName.get()}`}
              buttonIcon={
                (
                  <Icon
                    height="24"
                    icon="lucide:user-round"
                    width="24"
                  />
                )
              }
            />
          </div>
        </div>
      </div>

      <ChangePasswordDialog
        isOpenInitially={isOpen}
        onClose={() => setIsOpen(false)}
      />

    </header>
  )
}
